/**
 * Shareable query links (Task 192) — pure helpers. A share URL carries ONLY a
 * standalone question (`/chat?q=…`); never answer text, history, citations,
 * model metadata, or any identifier (the privacy contract). For a rewritten
 * follow-up, the shared question is the RESOLVED standalone, not the raw fragment.
 */

/** Max chars we'll put in / read from a share URL (matches the answer cap). */
export const SHARE_QUERY_MAX = 1000;

/**
 * The question to share for an answered turn: the rewrite's resolved
 * standalone_query when present (so "¿y el siguiente?" shares "…segundo sello"),
 * else the user's original text. Returns null when neither yields a non-empty
 * question (don't share a raw unresolved fragment with no resolution).
 */
export function shareQuestionForTurn(args: { userText: string; standaloneQuery?: string | null | undefined }): string | null {
  const resolved = typeof args.standaloneQuery === "string" ? args.standaloneQuery.trim() : "";
  if (resolved.length > 0) return resolved.slice(0, SHARE_QUERY_MAX);
  const raw = args.userText.trim();
  return raw.length > 0 ? raw.slice(0, SHARE_QUERY_MAX) : null;
}

/** Build the shareable URL. `/chat` routes to the chat view (any non-/corpus
 * path does). Only the `q` param is set — nothing else. */
export function buildShareUrl(origin: string, question: string): string {
  const cleanOrigin = origin.replace(/\/+$/, "");
  return `${cleanOrigin}/chat?q=${encodeURIComponent(question.trim().slice(0, SHARE_QUERY_MAX))}`;
}

/** Read a shared question from a URL search string (e.g. window.location.search).
 * Returns the trimmed, capped question, or null when absent/empty. */
export function parseSharedQuery(search: string): string | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const raw = params.get("q");
  if (raw === null) return null;
  const q = raw.trim().slice(0, SHARE_QUERY_MAX);
  return q.length > 0 ? q : null;
}

/** A new search string with the `q` param removed (other params preserved), so
 * we can clean the URL after capturing a shared question — a reload then won't
 * re-prefill a stale question, and the visible URL stays tidy. */
export function searchWithoutShareQuery(search: string): string {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return search;
  }
  params.delete("q");
  const s = params.toString();
  return s.length > 0 ? `?${s}` : "";
}
